/**
 * A direct copy of the Node interface from the DOM spec.
 * This is used to avoid using the global Node type.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Node
 * @see https://dom.spec.whatwg.org/#interface-node
 */
var NodeKind;
(function (NodeKind) {
    /** The node is an element. */
    NodeKind[NodeKind["ELEMENT_NODE"] = 1] = "ELEMENT_NODE";
    /** The node is a Text node. */
    NodeKind[NodeKind["TEXT_NODE"] = 3] = "TEXT_NODE";
    /** The node is a Comment node. */
    NodeKind[NodeKind["COMMENT_NODE"] = 8] = "COMMENT_NODE";
    /** The node is a document. */
    NodeKind[NodeKind["DOCUMENT_NODE"] = 9] = "DOCUMENT_NODE";
    /** The node is a doctype. */
    NodeKind[NodeKind["DOCUMENT_TYPE_NODE"] = 10] = "DOCUMENT_TYPE_NODE";
    /** The node is a DocumentFragment node. */
    NodeKind[NodeKind["DOCUMENT_FRAGMENT_NODE"] = 11] = "DOCUMENT_FRAGMENT_NODE";
})(NodeKind || (NodeKind = {}));
export function isComment(value) {
    return isNodeWithType(value, NodeKind.COMMENT_NODE);
}
export function isCSSMediaRule(value) {
    return isNonNullableObject(value) && "media" in value && "cssRules" in value;
}
export function isCSSStyleDeclaration(value) {
    return (isNonNullableObject(value) && "cssText" in value && "setProperty" in value);
}
export function isCSSStyleRule(value) {
    return (isNonNullableObject(value) && "selectorText" in value && "style" in value);
}
export function isCSSStyleSheet(value) {
    return (isNonNullableObject(value) && "cssRules" in value && "insertRule" in value);
}
export function isDocumentFragment(value) {
    return isNodeWithType(value, NodeKind.DOCUMENT_FRAGMENT_NODE);
}
export function isElement(value) {
    return isNodeWithType(value, NodeKind.ELEMENT_NODE);
}
/**
 * Check if a string ends with a given search string.
 *
 * @remarks
 *
 * This is a micro-optimized version of the `String.prototype.endsWith` method.
 * It uses `slice` to check if the string ends with the specified search string.
 * It's twice as fast as the native `endsWith` in the cases we care about.
 *
 * @internal scope: workspace
 */
/*
 * ### Private Remarks
 *
 * IDC if you think micro-optimizing this is a bad idea, argue with your mom about it.
 */
function endsWith(value, searchString) {
    return value.slice(searchString.length * -1) === searchString;
}
export function isElementWithNSSuffix(value, namespaceSuffix) {
    return (isElement(value) &&
        "namespaceURI" in value &&
        typeof value.namespaceURI === "string" &&
        endsWith(value.namespaceURI, namespaceSuffix));
}
export function isHTMLElement(value) {
    return isElementWithNSSuffix(value, "html") && "style" in value;
}
export function isCustomElementConstructor(value) {
    return (typeof value === "function" &&
        "prototype" in value &&
        Object.prototype.isPrototypeOf.call(HTMLElement, value));
}
export function isNode(value) {
    return (isNonNullableObject(value) && "nodeType" in value && "nodeName" in value);
}
export function isNodeWithType(value, nodeType) {
    return isNode(value) && value.nodeType === nodeType;
}
export function isNonNullableObject(value) {
    return typeof value === "object" && value !== null;
}
export function isPromiseLike(value) {
    return (isNonNullableObject(value) &&
        "then" in value &&
        typeof value.then === "function");
}
export function isShadowRoot(value) {
    return (isNonNullableObject(value) && "host" in value && isDocumentFragment(value));
}
export function isDocument(value) {
    return isNodeWithType(value, NodeKind.DOCUMENT_NODE);
}
export function isSVGElement(value) {
    return isElementWithNSSuffix(value, "svg");
}
export function isText(value) {
    return isNodeWithType(value, NodeKind.TEXT_NODE);
}
export function isUnknownDictionary(value) {
    return isNonNullableObject(value) && !Array.isArray(value);
}
export function isCustomElement(value) {
    return (isHTMLElement(value) &&
        // All custom elements have a tag name that contains a hyphen.
        value.tagName.includes("-"));
}
export function isTemplateStringsArray(value) {
    return (Array.isArray(value) &&
        // All template strings arrays have at least one string element.
        value.length > 0 &&
        typeof value[0] === "string" &&
        // All template strings arrays have a `raw` property
        "raw" in value &&
        value.raw !== undefined);
}
